import { AppDispatch, RootState } from '@/store/store'
import { useDispatch, useSelector } from 'react-redux'
import {
	clearNotifications,
	markAllAsRead,
	removeNotificationOrder,
} from '@/store/features/notifications'
import {
	Box,
	Text,
	VStack,
	HStack,
	Button,
	Flex,
	Badge,
} from '@chakra-ui/react'
import Icon from '../shared/icons'
import { useState, useRef, useEffect } from 'react'
import OrdersEntity from '@/entities/api/orders/orders'
import DateConvert from '@/utils/date-convert'
import { useNavigate } from 'react-router-dom'
import { setOrderFn } from '@/store/features/orders'
import { OrderStatusColor, OrderStatusConvert } from '@/utils/order-status'

const NotificationsPopup = () => {
	const { unreadCount, notifiedOrders } = useSelector(
		(state: RootState) => state.notifications
	)
	const dispatch = useDispatch<AppDispatch>()
	const [isOpen, setIsOpen] = useState(false)
	const popupRef = useRef<HTMLDivElement>(null)
	const navigation = useNavigate()

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				popupRef.current &&
				!popupRef.current.contains(event.target as Node)
			) {
				setIsOpen(false)
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		} else {
			document.removeEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	const handleTogglePopup = () => {
		setIsOpen(!isOpen)
		if (!isOpen && unreadCount > 0) {
			dispatch(markAllAsRead())
		}
	}

	const handleRemoveNotification = (orderId: string, e: React.MouseEvent) => {
		e.stopPropagation()
		dispatch(removeNotificationOrder(orderId))
	}

	const handleClearAll = () => {
		dispatch(clearNotifications())
	}

	const selectOrder = (order: OrdersEntity) => {
		dispatch(setOrderFn(order))
		navigation('/orders')
	}

	return (
		<Box position='relative' ref={popupRef}>
			<Box position='relative' onClick={handleTogglePopup} cursor='pointer'>
				<Icon icon='notifications' size='xl' color='black' />
				{unreadCount > 0 && (
					<Box
						as={Badge}
						position='absolute'
						top='-6px'
						right='-6px'
						bg='red.500'
						color='white'
						fontSize='xs'
						borderRadius='full'
						minW='18px'
						h='18px'
						display='flex'
						alignItems='center'
						justifyContent='center'
						p='1px'
						zIndex='1'
					>
						{unreadCount}
					</Box>
				)}
			</Box>

			{isOpen && (
				<Box
					position='absolute'
					top='100%'
					right='0'
					mt='12px'
					bg='white'
					border='1px'
					borderColor='gray.200'
					borderRadius='2xl'
					boxShadow='0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
					zIndex='1000'
					minW={{ base: '340px', md: '420px' }}
					maxW={{ base: '95vw', md: '480px' }}
					maxH={{ base: '85vh', md: '600px' }}
					overflowY='hidden'
				>
					<Box
						px={{ base: '5', md: '6' }}
						py='5'
						borderBottom='1px'
						borderColor='gray.100'
						bg='gray.50'
						roundedTop='2xl'
					>
						<Flex justify='space-between' align='center'>
							<VStack align='flex-start' gap='1'>
								<Text
									fontSize={{ base: 'lg', md: 'xl' }}
									fontWeight='bold'
									color='gray.900'
									lineHeight='1.2'
								>
									Notificaciones
								</Text>
								{notifiedOrders.length > 0 && (
									<Text fontSize='sm' color='gray.600'>
										{notifiedOrders.length}{' '}
										{notifiedOrders.length === 1 ? 'orden' : 'órdenes'}
									</Text>
								)}
							</VStack>
							{notifiedOrders.length > 0 && (
								<Button
									size='sm'
									variant='ghost'
									colorScheme='red'
									onClick={handleClearAll}
									fontSize='sm'
									fontWeight='medium'
									px='4'
									py='2'
									h='auto'
									rounded='xl'
									_hover={{ bg: 'red.50' }}
								>
									Limpiar todo
								</Button>
							)}
						</Flex>
					</Box>

					<Box w='full' p='4'>
						<Box
							bg='gray.50'
							border='0.5px solid gray'
							borderRadius='md'
							maxH={{ base: 'calc(85vh - 120px)', md: '480px' }}
							overflowY='auto'
							css={{
								'&::-webkit-scrollbar': {
									width: '6px',
								},
								'&::-webkit-scrollbar-track': {
									background: '#f7fafc',
								},
								'&::-webkit-scrollbar-thumb': {
									background: '#cbd5e0',
									borderRadius: '3px',
								},
								'&::-webkit-scrollbar-thumb:hover': {
									background: '#a0aec0',
								},
							}}
						>
							{notifiedOrders.length === 0 ? (
								<Box p='12' textAlign='center'>
									<Box
										w='16'
										h='16'
										mx='auto'
										mb='4'
										bg='gray.100'
										rounded='full'
										display='flex'
										alignItems='center'
										justifyContent='center'
									>
										<Icon icon='notifications' size='xl' color='gray.400' />
									</Box>
									<Text
										fontSize='lg'
										fontWeight='semibold'
										color='gray.900'
										mb='2'
									>
										No hay notificaciones
									</Text>
									<Text fontSize='sm' color='gray.500'>
										Todas las notificaciones aparecerán aquí
									</Text>
								</Box>
							) : (
								<VStack gap='0' align='stretch'>
									{notifiedOrders.map((order: OrdersEntity, index: number) => (
										<Box
											key={order._id}
											px={{ base: '3', md: '3' }}
											py='3'
											borderBottom={
												index < notifiedOrders.length - 1 ? '1px' : 'none'
											}
											borderColor='gray.100'
											_hover={{ bg: 'gray.50' }}
											cursor='pointer'
											transition='all 0.2s'
											onClick={() => selectOrder(order)}
										>
											<Flex justify='space-between' align='flex-start' gap='4'>
												<Flex gap='4' flex='1' minW='0'>
													<Box flex='1' minW='0'>
														<Flex
															align={{ base: 'flex-start', md: 'center' }}
															justify='space-between'
															mb='3'
															direction={{ base: 'column', md: 'row' }}
															gap={{ base: '2', md: '0' }}
														>
															<Text
																fontSize={{ base: 'md', md: 'lg' }}
																fontWeight='semibold'
																color='gray.900'
																lineHeight='1.3'
															>
																{order.customer.name} {order.customer.lastname}
															</Text>
															<Badge
																colorScheme={OrderStatusColor(order.status)}
																size='sm'
																borderRadius='lg'
																px='3'
																py='1'
																fontSize='xs'
																fontWeight='medium'
																textTransform='capitalize'
															>
																{OrderStatusConvert(order.status)}
															</Badge>
														</Flex>

														<VStack align='flex-start' gap='2' mb='3'>
															<HStack gap='2'>
																<Text
																	fontSize='sm'
																	color='gray.500'
																	fontWeight='medium'
																>
																	Pedido:
																</Text>
																<Text
																	fontSize='sm'
																	color='gray.700'
																	fontFamily='mono'
																>
																	#{order?.code}
																</Text>
															</HStack>

															{order.products && order.products.length > 0 && (
																<HStack gap='2'>
																	<Text
																		fontSize='sm'
																		color='gray.500'
																		fontWeight='medium'
																	>
																		Productos:
																	</Text>
																	<Text fontSize='sm' color='gray.700'>
																		{order.products.length}{' '}
																		{order.products.length === 1
																			? 'item'
																			: 'items'}
																	</Text>
																</HStack>
															)}

															<HStack gap='2'>
																<Text
																	fontSize='sm'
																	color='gray.500'
																	fontWeight='medium'
																>
																	Fecha:
																</Text>
																<Text fontSize='sm' color='gray.700'>
																	{DateConvert(order?._created ?? '')}
																</Text>
															</HStack>
														</VStack>

														{order.payment_info &&
															order.payment_info.amount && (
																<Box
																	bg='gray.50'
																	px='4'
																	py='3'
																	rounded='xl'
																	border='1px'
																	borderColor='gray.200'
																>
																	<HStack justify='space-between'>
																		<Text
																			fontSize='sm'
																			color='gray.600'
																			fontWeight='medium'
																		>
																			Total:
																		</Text>
																		<Text
																			fontSize='md'
																			fontWeight='bold'
																			color='gray.900'
																		>
																			$
																			{parseFloat(
																				order.payment_info.amount
																			).toLocaleString('es-CO')}{' '}
																			{order.payment_info.currency}
																		</Text>
																	</HStack>
																</Box>
															)}
													</Box>
												</Flex>

												<VStack gap='2' flexShrink='0'>
													<Button
														aria-label='Ver orden'
														size='sm'
														variant='ghost'
														colorScheme='blue'
														rounded='lg'
														w='8'
														h='8'
														minW='8'
														p='0'
														display='flex'
														alignItems='center'
														justifyContent='center'
														onClick={(e) => {
															e.stopPropagation()
															console.log('Navegar a orden:', order._id)
														}}
														_hover={{ bg: 'blue.50' }}
													>
														<Icon icon='eye' size='sm' color='blue.600' />
													</Button>
													<Button
														aria-label='Eliminar notificación'
														size='sm'
														variant='ghost'
														colorScheme='red'
														rounded='lg'
														w='8'
														h='8'
														minW='8'
														p='0'
														display='flex'
														alignItems='center'
														justifyContent='center'
														onClick={(e) =>
															handleRemoveNotification(order._id, e)
														}
														_hover={{ bg: 'red.50' }}
													>
														<Icon icon='times' size='sm' color='red.600' />
													</Button>
												</VStack>
											</Flex>
										</Box>
									))}
								</VStack>
							)}
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	)
}

export default NotificationsPopup
